import React, { useEffect, useRef, useState } from 'react';
import useFormScrolling from '../../hooks/useFormScrolling';
import { motion } from 'framer-motion';
import { useRegistration } from '../../context/RegistrationContext';
import { useModal } from '../../context/ModalContext';
import { Button } from '../../components/common/Button';
import { getDielne } from '../../api/letavyApi';

const WorkshopCard = ({ dielna, onSelect }) => {
  const spotsLeft = dielna.volnych;
  const capacity = dielna.kapacita || 1; // Ochrana proti deleniu nulou
  const percentFree = (spotsLeft / capacity) * 100;
  const isAvailable = spotsLeft > 0;
  
  // Určenie farby indikátora podľa percentuálneho pomeru voľných miest
  // Použitá farebná schéma je optimalizávaná pre prístupnosť (vrchátane ľudí s poruchami vnímania farieb)
  const getStatusStyles = () => {
    if (spotsLeft <= 0) return 'bg-slate-200 text-slate-800 ring-2 ring-red-500'; // 0% - tmavosivá + červený okraj
    if (percentFree < 25) return 'bg-purple-100 text-purple-800'; // <25% - fialová (odlišná od ostatných)
    if (percentFree < 50) return 'bg-orange-100 text-orange-800 ring-2 ring-orange-500'; // <50% - modrá
    if (percentFree < 75) return 'bg-blue-100 text-blue-800 ring-2 ring-blue-500'; // <75% - modrá
    return 'bg-green-100 text-green-800'; // >=75% - zelená
  };

  return (
    <div 
      className={`bg-white rounded-lg shadow border-l-4 ${isAvailable ? 'border-primary' : 'border-gray-300'} 
      overflow-hidden transition-all duration-200 ${!isAvailable ? 'opacity-70 grayscale' : ''}`}
    >
      {/* Hlavička s názvom a voľnými miestami */}
      <div className="p-4 pb-2 relative">
        <div className="absolute top-2 right-2">
          <span 
            className={`inline-flex items-center justify-center w-7 h-7 rounded-full font-medium text-sm ${getStatusStyles()}`}
            title={`${spotsLeft} voľných miest z ${capacity} (${Math.round(percentFree)}%)`}
          >
            {spotsLeft}
          </span>
        </div>
        
        <h3 className={`text-lg font-medium pr-8 ${!isAvailable ? 'text-gray-500' : ''}`}>{dielna.nazov2}</h3>
      </div>
      
      {/* Informácie o kapacite a tlačidlo */}
      <div className="px-4 pb-3 pt-1 flex items-center justify-between">
        <div>
          <span className="text-xs text-gray-500">Kapacita: {dielna.kapacita} &nbsp;/&nbsp; Voľných: {spotsLeft}</span>
        </div>
        
        {isAvailable ? (
          <Button
            variant="default"
            size="sm"
            onClick={() => onSelect(dielna)}
          >
            Vybrať
          </Button>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
            Obsadené
          </span>
        )}
      </div>
    </div>
  );
};

const WorkshopSelectionPage = () => {
  const { state, dispatch, actionTypes } = useRegistration();
  const { confirmAction } = useModal();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const errorRef = useRef(null);

  useEffect(() => {
    if (!error) return;
    const id = setTimeout(() => {
      if (!errorRef.current) return;
      errorRef.current.focus({ preventScroll: true });
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
    return () => clearTimeout(id);
  }, [error]);

  // Aktivujeme hook pre automatické scrollovanie formulárov na mobilných zariadeniach
  useFormScrolling();
  
  // Načítanie dielní pri prvom renderovaní
  useEffect(() => {
    const fetchDielne = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Extrakcia dátumu narodenia z formulára
        const datumNarodenia = state.formData.datum_narodenia || '1.1.2024';
        // Preformátovanie dátumu z YYYY-MM-DD na DD.MM.YYYY
        const parts = datumNarodenia.split('-');
        const formattedDate = parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : datumNarodenia;
        
        const dielne = await getDielne(formattedDate);
        dispatch({ type: actionTypes.SET_DIELNE, payload: dielne });
      } catch (err) {
        console.error('Chyba pri načítaní dielní:', err);
        setError('Nepodarilo sa načítať zoznam dielní. Skúste to prosím neskôr.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDielne();
  }, [dispatch, actionTypes, state.formData.datum_narodenia, retryCount]);
  
  const handleSelectDielna = (dielna) => {
    dispatch({ type: actionTypes.SET_SELECTED_DIELNA, payload: dielna });
    dispatch({ type: actionTypes.SET_STEP, payload: 4 });
  };
  
  const handleBack = () => {
    dispatch({ type: actionTypes.SET_STEP, payload: 2 });
  };

  const handleRestart = () => {
    confirmAction(
      'Začať odznova', 
      'Skutočne chcete začať registráciu odznova? Všetky doteraz zadané údaje budú stratené.', 
      () => {
        dispatch({ type: actionTypes.RESET_STATE });
        dispatch({ type: actionTypes.SET_STEP, payload: 1 });
      },
      { confirmText: 'Začať odznova', variant: 'warning' }
    );
  };

  return (
    <motion.div 
      className="container mx-auto px-4 py-8 max-w-4xl"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.3 }}
    >
      <div className="bg-white rounded-lg shadow-lg p-6 form-container">
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-2">
          Výber dielne
        </h1>
        <p className="text-gray-600 mb-6">
          Vyberte si dielňu, do ktorej sa chcete prihlásiť. Počet voľných miest je obmedzený.
        </p>
        
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : error ? (
          <div
            ref={errorRef}
            tabIndex={-1}
            className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 outline-none"
          >
            <p className="text-red-700">{error}</p>
            <button 
              className="mt-2 text-red-700 underline"
              onClick={() => {
                setError(null);
                dispatch({ type: actionTypes.SET_DIELNE, payload: [] });
                setRetryCount(c => c + 1);
              }}
            >
              Skúsiť znova
            </button>
          </div>
        ) : (
          <>
            {state.dielne.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600">Žiadne dostupné dielne.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {state.dielne.map((dielna) => (
                  <WorkshopCard 
                    key={dielna.id} 
                    dielna={dielna} 
                    onSelect={handleSelectDielna} 
                  />
                ))}
              </div>
            )}
          </>
        )}
        
        <div className="flex justify-start mt-4 gap-2">
          <Button 
            variant="outline"
            onClick={handleBack}
          >
            Späť
          </Button>
          
          <Button 
            variant="outline"
            onClick={handleRestart}
            className="border-accent text-accent hover:bg-accent/10 hover:text-accent focus-visible:ring-accent"
          >
            Začať odznova
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default WorkshopSelectionPage;
